import { onAuthStateChanged, type User } from 'firebase/auth';
import { createContext, type ReactNode, use, useEffect, useRef, useState } from 'react';
import { clearQueryResultCache } from '@/features/metric-hub/explore/query-result-cache';
import { auth } from '@/lib/firebase';

interface AuthState {
  user: User | null;
  isLoading: boolean;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ user: null, isLoading: true });
  const hadUser = useRef(false);

  useEffect(() => {
    return onAuthStateChanged(auth, (user) => {
      // Sign-out (had a user, now none): drop the persisted query cache so
      // metric results never linger in localStorage on a shared machine.
      if (hadUser.current && !user) clearQueryResultCache();
      hadUser.current = !!user;
      setState({ user, isLoading: false });
    });
  }, []);

  return <AuthContext value={state}>{children}</AuthContext>;
}

export function useAuth(): AuthState {
  const ctx = use(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an <AuthProvider>');
  }
  return ctx;
}
